import React, { useState, useEffect } from "react";
import { assetUrl } from "../utils/assets";

const heroImages = [
  {
    src: "/images/hero/ds_2i_h1.jpeg",
    productId: "landscape-series-4",
    alt: "Landscape Series painting by Mike Terpeza (Michael Terpeza)",
  },
  {
    src: "/images/hero/stickerBanner.jpeg",
    mobileSrc: "/images/hero/stickerBanner_mobile.png",
    href: "/store#stickers-section",
    alt: "Hello Stickers — original art stickers by Mike Terpeza (Michael Terpeza)",
    imgStyle: {
      transform: "scale(1.12)",
      objectPosition: "25% center",
    },
  },
  {
    src: "/images/hero/dotSeries_8i_h2_i.jpeg",
    productId: "dot-series-3",
    alt: "Dot Series painting by Mike Terpeza (Michael Terpeza)",
  },
  {
    src: "/images/hero/ids_6i_h2.jpeg",
    productId: "landscape-series-5",
    alt: "Landscape Series painting by Mike Terpeza (Michael Terpeza)",
  },
];

export default function HeroSection({ theme }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = heroImages.length;
  const isDark = theme === "dark";

  useEffect(() => {
    if (paused) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 3500);
    return () => clearInterval(interval);
  }, [total, paused]);

  // Carousel navigation
  const prev = () => setCurrent((prev) => (prev - 1 + total) % total);
  const next = () => setCurrent((prev) => (prev + 1) % total);

  return (
    <section
      className="relative h-[420px] sm:h-[540px] md:h-[700px] bg-gray-200 overflow-hidden"
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured artwork slideshow"
    >
      {heroImages.map((slide, idx) => {
        const imgClass = `w-full h-full absolute inset-0 transition-opacity duration-500 object-cover ${slide.imgStyle?.objectPosition ? "" : "object-center"} ${idx === current ? "opacity-100" : "opacity-0"}`;
        const imgProps = {
          alt: slide.alt || `Mike Terpeza artwork - hero ${idx + 1}`,
          className: imgClass,
          style: slide.imgStyle || undefined,
          fetchPriority: idx === 0 ? "high" : "low",
          decoding: "async",
          loading: idx === 0 ? "eager" : "lazy",
        };
        if (slide.mobileSrc) {
          return (
            <picture
              key={slide.src}
              className={`w-full h-full absolute inset-0 ${idx === current ? "opacity-100" : "opacity-0"} transition-opacity duration-500`}
            >
              {/* tablet and smaller */}
              <source
                media="(max-width: 1023px)"
                srcSet={assetUrl(slide.mobileSrc)}
              />
              {/* desktop */}
              <source
                media="(min-width: 1024px)"
                srcSet={assetUrl(slide.src)}
              />
              <img src={assetUrl(slide.src)} alt={imgProps.alt} {...imgProps} />
            </picture>
          );
        }
        return (
          <img
            key={slide.src}
            src={assetUrl(slide.src)}
            alt={imgProps.alt}
            {...imgProps}
          />
        );
      })}
      {/* Clickable overlay — links to the current slide's product page */}
      <a
        href={
          heroImages[current].href || `/shop/${heroImages[current].productId}`
        }
        aria-label="View product details"
        className="absolute inset-0 z-10 cursor-pointer"
      />
      <div className="absolute inset-x-0 bottom-0 md:inset-x-4 md:bottom-3 md:right-10 md:left-auto md:ml-auto z-20 w-full md:max-w-xl rounded-none md:rounded-xl border-0 md:border border-white/30 bg-black/35 md:bg-black/45 backdrop-blur-md px-3 py-2 md:p-6 text-white text-right shadow-none md:shadow-xl">
        <h2
          className={`text-lg md:text-3xl font-bold leading-tight mb-1 md:mb-2 ${isDark ? "text-[#6cebe4]" : "text-gray-200"}`}
        >
          Now in the store
        </h2>
        <p className="text-xs md:text-base text-white/90 leading-snug md:leading-relaxed mb-2 md:mb-4">
          Stickers, paintings, drawings and mixed media. Please contact me for
          pricing and to purchase paintings, drawings and mixed media.
        </p>
        <div className="flex flex-wrap gap-1.5 md:gap-2 justify-end">
          <a
            className={`inline-flex items-center rounded-md font-semibold px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm hover:brightness-95 transition ${
              isDark ? "bg-[#6cebe4] text-gray-900" : "bg-[#ff4000] text-white"
            }`}
            href="/shop"
          >
            Shop now
          </a>
        </div>
      </div>
      {/* Carousel controls */}
      <button
        onClick={prev}
        className="absolute left-2 top-1/2 z-20 transform -translate-y-1/2 bg-white bg-opacity-75 p-2 rounded-full"
        aria-label="Previous slide"
      >
        ‹
      </button>
      <button
        onClick={next}
        className="absolute right-2 top-1/2 z-20 transform -translate-y-1/2 bg-white bg-opacity-75 p-2 rounded-full"
        aria-label="Next slide"
      >
        ›
      </button>
      {/* Pause / play — WCAG 2.2.2 */}
      <div className="absolute bottom-2 left-2 z-20">
        <button
          onClick={() => setPaused((p) => !p)}
          className="bg-white/70 hover:bg-white/90 rounded-full p-1.5 text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
          aria-label={paused ? "Resume slideshow" : "Pause slideshow"}
        >
          {paused ? (
            <svg
              viewBox="0 0 16 16"
              fill="currentColor"
              className="w-3.5 h-3.5"
              aria-hidden="true"
            >
              <path d="M3 2.5l10 5.5-10 5.5V2.5z" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 16 16"
              fill="currentColor"
              className="w-3.5 h-3.5"
              aria-hidden="true"
            >
              <path d="M4 3h2.5v10H4zm5.5 0H12v10H9.5z" />
            </svg>
          )}
        </button>
      </div>
      {/* Slide dots — centered */}
      <div className="absolute bottom-2 inset-x-0 z-20 flex items-center justify-center gap-2">
        {heroImages.map((_, idx) => (
          <button
            key={idx}
            onClick={() => {
              setCurrent(idx);
              setPaused(true);
            }}
            className={`w-2 h-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white ${idx === current ? "bg-white" : "bg-white/45 hover:bg-white/75"}`}
            aria-label={`Go to slide ${idx + 1} of ${total}`}
            aria-current={idx === current ? "true" : undefined}
          />
        ))}
      </div>
    </section>
  );
}
